import { RootState } from '@app/store'
import {
	DarioSystemBGTargetRanges,
	DarioSystemGender,
	DarioSystemHtLastTestResults,
	DarioSystemReportedA1C,
} from '@app/types/darioSystemData'
import {
	getUsersTimeLocation,
	preciseRound,
	targetRangesForBGInMgOrMmol,
} from '@app/helpers'
import { selectTimeFormatter } from '@app/hooks/contexts/timeController/formatters'

export const selectPatientBirthdateUnix = (state: RootState): number | null => {
	const birthdate =
		state.appReducer.patientOverview?.darioSystemData?.birthDate || null

	return birthdate ? birthdate * 1000 : null
}

export const selectPatientBirthdate = (state: RootState): string => {
	const timeLocale = getUsersTimeLocation(
		state.appReducer?.user?.localization?.dateConfiguration
	)

	const dF = selectTimeFormatter(timeLocale, 'df')

	const birthdateUnix =
		state.appReducer.patientOverview?.darioSystemData?.birthDate

	const birthDateFormatted = birthdateUnix ? dF(birthdateUnix * 1000) : ''

	return birthDateFormatted || ''
}

export const selectPatientLanguages = (state: RootState): string =>
	state.appReducer.patientOverview?.darioSystemData?.language || ''

export const selectPatientUnits = (state: RootState): string =>
	state.appReducer.patientOverview?.darioSystemData?.units?.general ||
	'metric'

export const selectPatientBGUnits = (state: RootState): string =>
	state.appReducer.patientOverview?.darioSystemData?.units?.glucose ||
	'mmol/L'

export const selectPatientGender = (
	state: RootState
): DarioSystemGender | string =>
	state.appReducer.patientOverview?.darioSystemData?.gender || ''

export const selectPatientHeight = (
	state: RootState
): number | undefined | null =>
	state.appReducer.patientOverview?.darioSystemData?.height

export const selectPatientWeight = (state: RootState): number | null => {
	const weight = state.appReducer.patientOverview?.darioSystemData?.weight

	const weightToNumber = weight ? +weight : null

	return weightToNumber ? preciseRound(weightToNumber, 1) : null
}

export const selectPatientTargetWeight = (state: RootState): number | null => {
	const weight =
		state.appReducer.patientOverview?.darioSystemData?.weightTarget

	const weightToNumber = weight ? +weight : null

	return weightToNumber ? preciseRound(weightToNumber, 1) : null
}

export const selectPatientAge = (state: RootState): number | null =>
	state.appReducer.patientOverview?.darioSystemData?.birthDate || null
// todo: should return calculated age

export const selectPatientAddress = (state: RootState): string | null =>
	state.appReducer.patientOverview?.darioSystemData?.region || null

export const selectPatientPhone = (state: RootState): string =>
	state.appReducer.patientOverview?.coachSystemData?.phone || ''

export const selectDailyActivityLevel = (state: RootState): number | null =>
	state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.daily_activity || null

export const selectStressLevel = (state: RootState): number | null =>
	state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.stress_level || null

export const selectAlcoholConsumptionLevel = (
	state: RootState
): number | null =>
	state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.alcohol_consumption || null

export const selectPatientComorbities = (state: RootState): Array<string> =>
	state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.comorbidities || []

export const selectPatientMedications = (state: RootState): Array<string> =>
	state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.medications || []

export const selectPatientEthnicity = (state: RootState): Array<string> =>
	state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.ethnicity || []

export const selectPatientFamilyGenetics = (state: RootState): string =>
	state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.family_genetics || ''

export const selectPatientChallenges = (state: RootState): Array<string> => {
	const challenges =
		state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
			?.challenges || {}
	const challengesArray = []

	for (const key in challenges) {
		challengesArray.push(challenges[key])
	}

	return challengesArray
}

export const selectPatientDiabetesType = (state: RootState): string =>
	state.appReducer?.patientOverview?.darioSystemData?.diabetesType || ''

export const selectDateOfDiabetesDiagnosis = (
	state: RootState
): number | null => {
	return (
		state.appReducer?.patientOverview?.darioSystemData?.diabetesDiagnosed ||
		null
	)
}

export const selectDateOfDiabetesDiagnosisFormatted = (
	state: RootState
): string => {
	const timeLocale = getUsersTimeLocation(
		state.appReducer?.user?.localization?.dateConfiguration
	)

	const dF = selectTimeFormatter(timeLocale, 'df')

	const date =
		state.appReducer?.patientOverview?.darioSystemData?.diabetesDiagnosed ||
		null

	return date ? dF(date * 1000) : '-'
}

export const selectDiabetesProfileReportedA1CFormatted = (
	state: RootState
): string => {
	const timeLocale = getUsersTimeLocation(
		state.appReducer?.user?.localization?.dateConfiguration
	)

	const dF = selectTimeFormatter(timeLocale, 'df')

	const reportedA1C =
		state.appReducer?.patientOverview?.darioSystemData?.hba1c ||
		({} as DarioSystemReportedA1C)

	if (reportedA1C.value && reportedA1C.date) {
		const date = dF(reportedA1C.date * 1000)

		return `${reportedA1C.value}% (${date})`
	}

	return ''
}

export const selectDiabetesProfileReportedA1C = (
	state: RootState
): number | null =>
	state.appReducer?.patientOverview?.darioSystemData?.hba1c?.value || null

export const selectInsulinTreatment = (state: RootState): string =>
	state.appReducer?.patientOverview?.darioSystemData?.insulinTreatment || ''

export const selectBolusInsulin = (state: RootState): string =>
	state.appReducer?.patientOverview?.darioSystemData?.insulin?.bolus || ''

export const selectBasalInsulin = (state: RootState): string =>
	state.appReducer?.patientOverview?.darioSystemData?.insulin?.basal || ''

export const selectOralMedicationsString = (state: RootState): string => {
	const oralMedications =
		state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
			?.oral_medications || []

	return oralMedications.join(', ')
}

export const selectTargetRanges = (
	state: RootState
): DarioSystemBGTargetRanges => {
	const measureUnits =
		state.appReducer?.patientOverview?.darioSystemData?.units?.glucose ||
		'mmol/L'

	return targetRangesForBGInMgOrMmol(
		measureUnits,
		state.appReducer?.patientOverview?.darioSystemData?.targetRanges
	)
}

export const selectDiagnosedWithHt = (state: RootState): string =>
	state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.property_diagnosed_with_ht || ''

export const selectDateDiagnosedWithHt = (state: RootState): number =>
	state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.property_date_diagnosed_with_ht || 0

export const selectHtMedications = (state: RootState): string => {
	const medications =
		state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
			?.property_ht_medications_meds_list || []

	return [...medications].join(', ')
}

export const selectHTLastTestResults = (
	state: RootState
): DarioSystemHtLastTestResults | undefined => {
	return state.appReducer?.patientOverview?.darioSystemData?.additionalOptions
		?.property_ht_last_test_results
}
